import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { MdImage, MdVideocam } from 'react-icons/md';
import { getTemplates } from '../../../../redux/actions/Chatbox.action';

export default function Templates() {
  const dispatch = useDispatch();
  const chatbox = useSelector((state) => state.chatbox);
  const [activeTab, setActiveTab] = useState('APPROVED');

  // --- Cursor-based pagination state ---
  const [afterCursor, setAfterCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]); // history for "Previous"
  const [pageIndex, setPageIndex] = useState(1);

  // Reset pagination whenever the tab changes
  useEffect(() => {
    setAfterCursor(null);
    setCursorStack([]);
    setPageIndex(1);
  }, [activeTab]);

  // Fetch whenever tab or cursor changes
  useEffect(() => {
    dispatch(
      getTemplates({
        status: activeTab,
        limit: 9,
        after: afterCursor,
      }),
    );
  }, [activeTab, afterCursor]);

  const templateList = chatbox?.getTemplates?.data?.data?.data || [];
  const paging = chatbox?.getTemplates?.data?.data?.paging || {};
  const templatesCount = chatbox?.getTemplates?.data?.templatesCount || 0;
  const hasNext = Boolean(paging?.next);
  const hasPrev = cursorStack.length > 0;

  const getComponent = (template, type) =>
    template?.components?.find((c) => c.type?.toUpperCase() === type);

  const getComponentText = (template, type) =>
    getComponent(template, type)?.text || '';

  const getHeaderFormat = (template) => {
    const header = getComponent(template, 'HEADER');
    return header?.format?.toUpperCase() || null;
  };

  const getHeaderImageUrl = (template) => {
    const header = getComponent(template, 'HEADER');
    return header?.example?.header_handle?.[0] || '';
  };

  const getButtons = (template) =>
    getComponent(template, 'BUTTONS')?.buttons || [];

  const tabs = [
    { key: 'APPROVED', label: 'Approved Templates' },
    { key: 'PENDING', label: 'Pending Approval Templates' },
    { key: 'REJECTED', label: 'Rejected Templates' },
  ];

  const handleNextPage = () => {
    if (!hasNext) return;
    setCursorStack((prev) => [...prev, afterCursor]);
    setAfterCursor(paging?.cursors?.after || null);
    setPageIndex((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (!hasPrev) return;
    const stack = [...cursorStack];
    const prevCursor = stack.pop();
    setCursorStack(stack);
    setAfterCursor(prevCursor || null);
    setPageIndex((p) => Math.max(1, p - 1));
  };

  return (
    <>
      <div className="mt-[100px] d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div
          className="d-flex gap-2 bg-white p-1 rounded"
          style={{ border: '1px solid #dee2e6' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2 rounded fw-bold border-0 ${
                activeTab === tab.key
                  ? '!bg-theme-dark text-white'
                  : 'bg-transparent'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="bg-white shadow-shadows py-[6px] px-[16px] rounded-full">
          Total Records : <span className="fw-bold">{templatesCount}</span>
        </div>
      </div>

      <div className="mt-4 row g-3">
        {templateList.length > 0 ? (
          templateList.map((template) => (
            <div className="col-md-4" key={template.id || template.name}>
              <div
                className="bg-white rounded shadow-sm p-3 h-100"
                style={{ border: '1px solid #eee' }}
              >
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-bold">Name: {template.name}</span>
                </div>

                {(() => {
                  const format = getHeaderFormat(template);
                  if (format === 'IMAGE') {
                    const imgUrl = getHeaderImageUrl(template);
                    return imgUrl ? (
                      <div
                        className="rounded overflow-hidden mb-2"
                        style={{ height: '150px', background: '#eef0f4' }}
                      >
                        <img
                          src={imgUrl}
                          alt="header"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="rounded d-flex align-items-center justify-content-center mb-2"
                        style={{ height: '150px', background: '#eef0f4' }}
                      >
                        <MdImage
                          className="text-secondary"
                          style={{ fontSize: 42 }}
                        />
                      </div>
                    );
                  }
                  if (format === 'VIDEO') {
                    return (
                      <div
                        className="rounded d-flex align-items-center justify-content-center mb-2"
                        style={{ height: '150px', background: '#eef0f4' }}
                      >
                        <MdVideocam
                          className="text-secondary"
                          style={{ fontSize: 42 }}
                        />
                      </div>
                    );
                  }
                  if (format === 'TEXT') {
                    return (
                      <div className="fw-bold mb-2">
                        {getComponentText(template, 'HEADER')}
                      </div>
                    );
                  }
                  return null;
                })()}

                <p className="mb-1" style={{ whiteSpace: 'pre-line' }}>
                  {getComponentText(template, 'BODY')}
                </p>

                {getComponentText(template, 'FOOTER') && (
                  <p className="text-muted small">
                    {getComponentText(template, 'FOOTER')}
                  </p>
                )}

                {getButtons(template).length > 0 && (
                  <div className="d-flex flex-wrap gap-3 border-top pt-2 mt-2">
                    {getButtons(template).map((btn, i) => (
                      <span className="text-primary small" key={i}>
                        {btn.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-5 text-muted">
            No templates found in this category
          </div>
        )}
      </div>

      {(hasNext || hasPrev) && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <span className="text-muted small">Page {pageIndex}</span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!hasPrev}
              onClick={handlePrevPage}
            >
              ‹ Prev
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!hasNext}
              onClick={handleNextPage}
            >
              Next ›
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
